import Joi from 'joi';

export const PHONE_REGEX = /^\d{3}-?\d{3}-?\d{4}$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Tipo del formulario ────────────────────────────────────────────────────────

export interface IntegraFormData {
  codigoRegion: string;
  codigoProvincia: string;
  codigoMunicipio: string;
  direccion: string;

  // Beneficiary read-only fields (populated from API)
  sexo: string;
  regimen: string;
  nombreCompleto: string;
  nss: string;
  fechaNacimiento: string;
  edad: string;

  parentesco: string;
  telefono: string;
  celular: string;
  correoElectronico: string;
  correoElectronicoAlterno: string;
  nivelEducativo: string;
  situacionLaboral: string;
  estadoCivil: string;

  tieneTeaDiagnosticado: string;
  fechaDiagnostico: string;

  idProfesionalDiagnostico: number | '';
  tieneOtroDiagnosticoProfesional: boolean;
  otroProfesionalDiagnostico: string;

  recibeTerapias: string;
  centroTerapia: string;

  recibeTerapiaIdList: number[];
  tieneOtraTerapia: boolean;
  otraTerapia: string;

  evaluacionesAplicadasIdList: number[];
  tieneOtraEvaluacion: boolean;
  otraEvaluacionAplicada: string;

  comorbilidadIdList: number[];
  tieneOtraComorbilidad: boolean;
  otraComorbilidad: string;

  idCondicionSocioeconomica: string;
  esOtraSituacion: boolean;
  otraSituacion: string;
  recibeSubsidio: string;
  tipoSubsidio: string;

  consentimiento: string;
}

// ─── Joi Schema ─────────────────────────────────────────────────────────────────

export const phoneField = (crossFieldName: string) =>
  Joi.any()
    .custom((value: any, helpers: Joi.CustomHelpers) => {
      const v = (value ?? '') as string;
      if (v && !PHONE_REGEX.test(v)) {
        return helpers.error('phone.format');
      }
      const parent = (helpers.state.ancestors as any[])[0];
      if (!v && !parent[crossFieldName]) {
        return helpers.error('phone.cross');
      }
      return v;
    })
    .messages({
      'phone.format': 'Formato inválido (ej: 809-555-1234)',
      'phone.cross': 'Debe indicar al menos un teléfono o celular',
    });

export const integraFormSchema = Joi.object<IntegraFormData>({
  codigoProvincia: Joi.string().allow(''),
  codigoMunicipio: Joi.string().allow(''),
  direccion: Joi.string().min(5).required().messages({
    'string.empty': 'Campo requerido',
    'string.min': 'La dirección es demasiada corta',
    'any.required': 'Campo requerido',
  }),

  sexo: Joi.string().allow(''),
  regimen: Joi.string().allow(''),
  nombreCompleto: Joi.string().allow(''),
  nss: Joi.string().allow(''),
  fechaNacimiento: Joi.string().allow(''),
  edad: Joi.string().allow(''),

  parentesco: Joi.string().required().messages({
    'string.empty': 'Seleccione el parentesco',
    'any.required': 'Seleccione el parentesco',
  }),

  telefono: phoneField('celular'),
  celular: phoneField('telefono'),

  correoElectronico: Joi.string().allow('').pattern(EMAIL_REGEX).messages({
    'string.pattern.base': 'Correo electrónico inválido',
  }),
  correoElectronicoAlterno: Joi.string().allow('').pattern(EMAIL_REGEX).messages({
    'string.pattern.base': 'Correo electrónico inválido',
  }),
  nivelEducativo: Joi.string().allow(''),
  situacionLaboral: Joi.string().allow(''),
  estadoCivil: Joi.string().min(5).required().messages({
    'string.bae': 'Campo requerido',
    'string.empty': 'Campo requerido',
    'string.min': 'El campo es muy corto',
    'any.required': 'Campo requerido',
  }),

  tieneTeaDiagnosticado: Joi.string().required().messages({
    'string.empty': 'Campo requerido',
    'any.required': 'Campo requerido',
  }),

  fechaDiagnostico: Joi.when('tieneTeaDiagnosticado', {
    is: 'si',
    then: Joi.date().required().less('now').messages({
      'date.base': 'Campo requerido',
      'date.less': 'La fecha de diagnóstico no puede ser una fecha futura',
      'any.required': 'Campo requerido',
    }),
    otherwise: Joi.string().allow(''),
  }),

  idProfesionalDiagnostico: Joi.alternatives()
    .try(Joi.number().integer().positive(), Joi.string().allow(''))
    .optional(),

  tieneOtroDiagnosticoProfesional: Joi.boolean().default(false),

  otroProfesionalDiagnostico: Joi.when('tieneTeaDiagnosticado', {
    is: 'si',
    then: Joi.when('tieneOtroDiagnosticoProfesional', {
      is: true,
      then: Joi.string().required().messages({
        'string.empty': 'Especifique el profesional',
        'any.required': 'Especifique el profesional',
      }),
      otherwise: Joi.string().allow(''),
    }),
    otherwise: Joi.string().allow(''),
  }),

  recibeTerapias: Joi.when('tieneTeaDiagnosticado', {
    is: 'si',
    then: Joi.string().required().messages({
      'string.empty': 'Campo requerido',
      'any.required': 'Campo requerido',
    }),
    otherwise: Joi.string().allow(''),
  }),

  centroTerapia: Joi.when('tieneTeaDiagnosticado', {
    is: 'si',
    then: Joi.when('recibeTerapias', {
      is: 'si',
      then: Joi.string().required().messages({
        'string.empty': 'Campo requerido',
        'any.required': 'Campo requerido',
      }),
      otherwise: Joi.string().allow(''),
    }),
    otherwise: Joi.string().allow(''),
  }),

  recibeTerapiaIdList: Joi.array().items(Joi.number()).default([]),

  tieneOtraTerapia: Joi.boolean().default(false),

  otraTerapia: Joi.when('tieneTeaDiagnosticado', {
    is: 'si',
    then: Joi.when('recibeTerapias', {
      is: 'si',
      then: Joi.when('tieneOtraTerapia', {
        is: true,
        then: Joi.string().required().max(100).messages({
          'string.empty': 'Especifique la terapia',
          'string.max': 'El campo es muy largo',
          'any.required': 'Especifique la terapia',
        }),
        otherwise: Joi.string().allow(''),
      }),
      otherwise: Joi.string().allow(''),
    }),
    otherwise: Joi.string().allow(''),
  }),

  evaluacionesAplicadasIdList: Joi.array().items(Joi.number()).default([]),

  tieneOtraEvaluacion: Joi.boolean().default(false),

  otraEvaluacionAplicada: Joi.when('tieneTeaDiagnosticado', {
    is: 'si',
    then: Joi.when('tieneOtraEvaluacion', {
      is: true,
      then: Joi.string().required().max(100).messages({
        'string.empty': 'Especifique la evaluación',
        'string.max': 'El campo es muy largo',
        'any.required': 'Especifique la evaluación',
      }),
      otherwise: Joi.string().allow(''),
    }),
    otherwise: Joi.string().allow(''),
  }),

  comorbilidadIdList: Joi.array().items(Joi.number()).default([]),

  tieneOtraComorbilidad: Joi.boolean().default(false),

  otraComorbilidad: Joi.when('tieneOtraComorbilidad', {
    is: true,
    then: Joi.string().required().max(100).messages({
      'string.empty': 'Especifique la comorbilidad',
      'string.max': 'El campo es muy largo',
      'any.required': 'Especifique la comorbilidad',
    }),
    otherwise: Joi.string().allow(''),
  }),

  idCondicionSocioeconomica: Joi.string().required().messages({
    'string.empty': 'Campo requerido',
    'any.required': 'Campo requerido',
  }),

  esOtraSituacion: Joi.boolean().default(false),

  otraSituacion: Joi.when('esOtraSituacion', {
    is: true,
    then: Joi.string().required().messages({
      'string.empty': 'Campo requerido',
      'any.required': 'Campo requerido',
      'string.max': 'El campo es muy largo',
    }),
    otherwise: Joi.string().allow(''),
  }),

  recibeSubsidio: Joi.string().required().messages({
    'string.empty': 'El campo es requerido',
    'any.required': 'El campo es requerido',
  }),

  tipoSubsidio: Joi.when('recibeSubsidio', {
    is: 'si',
    then: Joi.string().required().messages({
      'string.empty': 'Indique el tipo de subsidio',
      'any.required': 'Indique el tipo de subsidio',
    }),
    otherwise: Joi.string().allow(''),
  }),

  consentimiento: Joi.string().required().messages({
    'string.empty': 'Debe aceptar o rechazar el consentimiento',
    'any.required': 'Debe aceptar o rechazar el consentimiento',
  }),
});
